/**
 * errors.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

// Closed list of plan validation error codes.
// Every value is an i18n key that must exist in every src/i18n/{lang}.js file.
export const PLAN_ERROR_CODES = Object.freeze({
  NO_SEGMENTS: 'plan_error_no_segments',
  INVALID_DEPTH: 'plan_error_invalid_depth',
  INVALID_TIME: 'plan_error_invalid_time',
  INVALID_GF: 'plan_error_invalid_gf',
  INVALID_GAS: 'plan_error_invalid_gas',
  NO_DILUENT: 'plan_error_no_diluent',
  INVALID_SETPOINT: 'plan_error_invalid_setpoint',
  NO_BAILOUT_GAS: 'plan_error_no_bailout_gas',
  // Physical settings of the planner itself (ascent / descent rates, stop
  // depths, ppO2 limits, gas switch duration): a negative or non finite value
  // silently shortens the plan, so it is refused instead of being computed.
  INVALID_CONFIG: 'plan_error_invalid_config',
  // Residual tissue state of a repetitive dive: a corrupted or hand edited
  // state would be read as "these tissues are clean" and produce a plan with
  // no decompression where the diver owes stops, so it is refused.
  INVALID_TISSUE_STATE: 'plan_error_invalid_tissue_state',
  // Surface interval between two dives of the same series: a negative or non
  // finite interval would either load the tissues backwards or leave them at
  // the previous dive level, both of which understate the decompression owed.
  INVALID_SURFACE_INTERVAL: 'plan_error_invalid_surface_interval',
  // Loop flush of a CCR dive: a depth at or below the bottom, a duration that
  // is not a duration, or two flushes fighting over the same metre. Refused
  // rather than repaired - a flush the engine quietly moves or drops would
  // have the diver breathing a mix he never chose.
  INVALID_FLUSH: 'plan_error_invalid_flush',
  // Segment time shorter than the descent it is meant to include (settings
  // descentTimeIncluded): the bottom time used to be clamped to 0 min without
  // a word, and the dive was planned as a bounce the diver never asked for.
  TIME_SHORTER_THAN_DESCENT: 'plan_error_time_shorter_than_descent',
  // Open circuit segment planned on a gas the diver disabled or removed since.
  // Refused rather than replaced by the richest mix reaching the depth: the
  // segment card and the printed sheet name the gas the diver picked, and the
  // table below them must be computed on that very gas.
  SEGMENT_GAS_UNAVAILABLE: 'plan_error_segment_gas_unavailable',
  // CCR plan handed to planBailout without the bailout point ccrPass records:
  // a plan from another tool or an older version. Refused rather than paired
  // with a guessed tissue state - the only state at hand after planCCR is the
  // one at the surface, and a bailout replayed from it is a bailout on tissues
  // that already offgased the whole ascent.
  NO_BAILOUT_POINT: 'plan_error_no_bailout_point'
})

// Closed list of plan warning codes carried by result.warnings.
// Every value is an i18n key that must exist in every src/i18n/{lang}.js file.
// These reuse the alert_* keys already translated in the six dictionaries.
export const PLAN_WARNING_CODES = Object.freeze({
  STOP_TIME_TRUNCATED: 'alert_stop_time_truncated',
  RUNTIME_LIMIT: 'alert_runtime_limit',
  DEFAULT_GAS_FALLBACK: 'alert_default_gas_fallback',
  // Repetitive dive entered before the previous one was cleared: the ceiling
  // is still below the surface when the diver gets back in the water.
  ENTRY_CEILING: 'alert_entry_ceiling',
  // Repetitive dive whose residual tissue state carries no load at all: it is
  // formally valid, physically impossible after a dive, and it produces a plan
  // shorter than the same dive planned on clean tissues.
  RESIDUAL_STATE_UNLOADED: 'alert_residual_state_unloaded',
  // No carried gas reaches the depth the plan holds the diver at: bestGas fell
  // back on the least hyperoxic one of the list, which is breathed past its
  // MOD. Typically a bailout list whose deepest mix stops above the bottom.
  GAS_ABOVE_MOD: 'alert_gas_above_mod',
  // Every cylinder that could be breathed at this depth is empty. The plan is
  // still produced - the diver has to surface - but it now prescribes gas
  // nobody is carrying, which must never be silent.
  GAS_EXHAUSTED: 'alert_gas_exhausted',
  // A cylinder ran dry on the way up and the ascent moved on to whatever was
  // left. The gas change printed in the table looks exactly like a planned deco
  // switch, and it is nothing of the sort: the diver did not choose it, and it
  // can go towards a leaner mix while ascending.
  GAS_SWITCH_ON_EMPTY: 'alert_gas_switch_on_empty',
  // Loop flush the final ascent never passed through, because the dive ends
  // shallower than the depth it was asked for. The plan is computed without
  // it, and the diver has to be told his flush did not happen.
  FLUSH_NOT_REACHED: 'alert_flush_not_reached',
  // Partial pressure of oxygen a loop flush actually produces, when it falls
  // outside the diver's own working band. A flush is the one moment of the
  // dive where the setpoint does not hold, so the ppO2 is whatever the flush
  // mix gives at that depth: air at 70 m is 1.70 bar, a 10/70 diluent at 6 m
  // is 0.16 bar, and neither is visible anywhere else in the plan.
  FLUSH_PPO2: 'alert_flush_ppo2',
  // Finishing the dive on oxygen is switched on in the settings, but the dive
  // never goes below the depth it was set at, so there is no ascent for it to
  // happen in. A 6 m flush on a 5 m dive is not a mistake worth refusing the
  // plan for, but it is a line the diver expects to read and will not find.
  O2_FLUSH_TOO_DEEP: 'alert_o2_flush_too_deep',
  // A cylinder line of the requirement table asks for more gas than the
  // cylinders of that mix hold. The table already prints INSUFFICIENT in red,
  // but a verdict that lives in one table reaches neither the alert block
  // above the plan nor the sheet metadata: this is the same verdict, carried
  // where every other danger of the plan is.
  GAS_INSUFFICIENT: 'alert_gas_insufficient'
})

// Validation error raised by the planning engine.
// `code` is an i18n key taken from PLAN_ERROR_CODES, `details` carries the
// faulty values so the UI can build a precise message.
export class PlanError extends Error {
  constructor(code, details = {}) {
    super(code)
    this.name = 'PlanError'
    this.code = code
    this.details = details
  }
}
